import React, { useEffect } from "react";
import { Form, Row, Col, Select, Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import ChevronDown from "@untitled-ui/icons-react/build/esm/ChevronDown";

export default function AccountingIFRSForm({
  form,
  initialValues = {},
  onFinish,
  ifrsClassificationOptions = [],
  accrualPostingFrequencyOptions = [],
  gLMapping = [],
  accountingBasisOptions = [],
  assetGL = [],
}) {
  
  // Submit normalization
  // Submit normalization
const handleFinish = (values) => {
  const glFields = [
    "principalGL",
    "accruedInterestGL",
    "interestIncomeGL",
    "unrealisedIncomeGL",
  ];

  const normalizedGL = {};

  glFields.forEach((field) => {
    // Simply pass the debit and credit IDs directly without wrapping in {id: ...}
    normalizedGL[field] = {
      debitAccount: values[field]?.debitAccount || null,
      creditAccount: values[field]?.creditAccount || null,
    };
  });

  onFinish({
    ...values,
    ...normalizedGL,
  });
};

  return (
    <div>
      <h3 style={{ marginBottom: 24, fontWeight: 600 }}>
        Accounting & IFRS Behaviour
      </h3>

      <Form layout="vertical" form={form} onFinish={handleFinish}>
        {/* IFRS Classification */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label={
                <span>
                  IFRS Classification
                  <Tooltip title="Determines measurement category, P&L vs OCI treatment, and impairment logic.">
                    <InfoCircleOutlined
                      style={{ marginLeft: 4, color: "#667085" }}
                    />
                  </Tooltip>
                </span>
              }
              name="ifrsClassification"
              rules={[{ required: true }]}
            >
              <Select
                style={{ height: 44 }}
                suffixIcon={<ChevronDown color="#667085" />}
                options={ifrsClassificationOptions}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label={
                <span>
                  Accounting Basis
                  <Tooltip title="Selects the valuation and posting engine for accounting purposes.">
                    <InfoCircleOutlined
                      style={{ marginLeft: 4, color: "#667085" }}
                    />
                  </Tooltip>
                </span>
              }
              name="accountingBasis"
              rules={[{ required: true }]}
            >
              <Select
                style={{ height: 44 }}
                suffixIcon={<ChevronDown color="#667085" />}
                options={accountingBasisOptions}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Revaluation */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Revaluation Required"
              name="revaluationRequiredFlag"
            >
              <Select
                style={{ height: 44 }}
                suffixIcon={<ChevronDown color="#667085" />}
                options={[
                  { label: "Yes", value: true },
                  { label: "No", value: false },
                ]}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label="Revaluation Frequency"
              name="revaluationFrequency"
            >
              <Select
                style={{ height: 44 }}
                suffixIcon={<ChevronDown color="#667085" />}
                options={accrualPostingFrequencyOptions}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* PRINCIPAL GL (Asset GL only) */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Principal Debit GL"
              name={["principalGL", "debitAccount"]}
              rules={[{ required: true }]}
            >
              <Select
                showSearch
                options={assetGL}
                optionFilterProp="label"
                style={{ height: 44 }}
                filterOption={(input, option) =>
                  option?.label?.toLowerCase().includes(input.toLowerCase())
                }
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label="Principal Credit GL"
              name={["principalGL", "creditAccount"]}
              rules={[{ required: true }]}
            >
              <Select
                showSearch
                options={assetGL}
                optionFilterProp="label"
                style={{ height: 44 }}
                filterOption={(input, option) =>
                  option?.label?.toLowerCase().includes(input.toLowerCase())
                }
              />
            </Form.Item>
          </Col>
        </Row>

        {/* ACCRUAL GL */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Accrual Debit GL"
              name={["accruedInterestGL", "debitAccount"]}
              rules={[{ required: true }]}
            >
              <Select
                showSearch
                options={gLMapping}
                optionFilterProp="label"
                style={{ height: 44 }}
                filterOption={(input, option) =>
                  option?.label?.toLowerCase().includes(input.toLowerCase())
                }
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label="Accrual Credit GL"
              name={["accruedInterestGL", "creditAccount"]}
              rules={[{ required: true }]}
            >
              <Select
                showSearch
                options={gLMapping}
                optionFilterProp="label"
                style={{ height: 44 }}
                filterOption={(input, option) =>
                  option?.label?.toLowerCase().includes(input.toLowerCase())
                }
              />
            </Form.Item>
          </Col>
        </Row>

        {/* REALISED INCOME GL */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Realised Income Debit GL"
              name={["interestIncomeGL", "debitAccount"]}
              rules={[{ required: true }]}
            >
              <Select
                showSearch
                options={gLMapping}
                optionFilterProp="label"
                style={{ height: 44 }}
                filterOption={(input, option) =>
                  option?.label?.toLowerCase().includes(input.toLowerCase())
                }
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label="Realised Income Credit GL"
              name={["interestIncomeGL", "creditAccount"]}
              rules={[{ required: true }]}
            >
              <Select
                showSearch
                options={gLMapping}
                optionFilterProp="label"
                style={{ height: 44 }}
                filterOption={(input, option) =>
                  option?.label?.toLowerCase().includes(input.toLowerCase())
                }
              />
            </Form.Item>
          </Col>
        </Row>

        {/* UNREALISED INCOME GL */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Unrealised Income Debit GL"
              name={["unrealisedIncomeGL", "debitAccount"]}
              rules={[{ required: true }]}
            >
              <Select
                showSearch
                options={gLMapping}
                optionFilterProp="label"
                style={{ height: 44 }}
                filterOption={(input, option) =>
                  option?.label?.toLowerCase().includes(input.toLowerCase())
                }
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label="Unrealised Income Credit GL"
              name={["unrealisedIncomeGL", "creditAccount"]}
              rules={[{ required: true }]}
            >
              <Select
                showSearch
                options={gLMapping}
                optionFilterProp="label"
                style={{ height: 44 }}
                filterOption={(input, option) =>
                  option?.label?.toLowerCase().includes(input.toLowerCase())
                }
              />
            </Form.Item>
          </Col>
        </Row>

        <button type="submit" hidden />
      </Form>
    </div>
  );
}
